import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScanBox from "@/components/ScanBox";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { getPage, pageUrl, alternatesFor, resolveLinks, ORIGIN, PAGES } from "@/content/seo";
import {
  orgAndWebSite,
  articleBlock,
  breadcrumbFromPath,
  definedTermSet,
  linkifySources,
  formatUpdatedFR,
  CONTENT_LAST_MODIFIED,
  DIRECTIVE_APPLICABLE_SINCE,
} from "@/lib/schemaOrg";
import { ArrowRight, BookOpenCheck, Scale, ChevronRight, ExternalLink, CalendarClock } from "lucide-react";

/**
 * SeoPage — gabarit unique des pages SEO/GEO (piliers pays, sectorielles, produit,
 * tarifs, scan gratuit). Un H1, des H2 en question, un paragraphe "réponse directe"
 * de 40-60 mots sous chaque H2, FAQPage JSON-LD, maillage interne à ancres descriptives.
 *
 * Lot 2 GEO (2026-09-30) :
 *   - Bloc Article (Schema.org) avec datePublished/dateModified/author/citation
 *   - Bloc BreadcrumbList
 *   - Bloc DefinedTermSet sur /conformite/termes-interdits
 *   - Bandeau « Mis à jour le… » sous chaque H1
 *   - Sources cliquables vers EUR-Lex, Légifrance, DGCCRF
 */
export default function SeoPage({ pageKey, lang }) {
  const { lang: current, setLang } = useLang();
  useEffect(() => { if (lang && lang !== current) setLang(lang); }, [lang, current, setLang]);

  const page = getPage(pageKey, lang);
  const canonical = pageUrl(pageKey, lang);
  const links = resolveLinks(page.links, lang);
  const sourceItems = linkifySources(page.sources);

  const faqLd = page.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage: lang,
        mainEntity: page.faq.map(([q, a]) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      }
    : null;

  const article = articleBlock({
    url: canonical,
    headline: page.h1,
    description: page.description,
    lang,
    datePublished: page.datePublished || "2026-05-04",
    dateModified: page.dateModified || CONTENT_LAST_MODIFIED,
  });

  const path = canonical.replace(ORIGIN, "") || "/";
  const breadcrumb = breadcrumbFromPath(path, page.h1, lang);

  const jsonLd = [orgAndWebSite(lang), article, breadcrumb];
  if (pageKey === "banned-terms") jsonLd.push(definedTermSet(lang, canonical));
  if (faqLd) jsonLd.push(faqLd);

  useSeoMeta({
    title: page.title,
    description: page.description,
    keywords: page.keywords,
    canonical,
    alternates: alternatesFor(pageKey),
    jsonLd,
  });

  const showFreshness = (() => {
    const p = PAGES.find((x) => x.key === pageKey);
    const g = p?.group;
    return ["pillar", "sector", "editorial"].includes(g) || pageKey === "product" || pageKey === "pricing";
  })();

  return (
    <div className="App min-h-screen flex flex-col bg-background">
      <Header variant="public" />
      <main className="flex-1" data-testid={`seo-page-${pageKey}`}>
        {/* HERO */}
        <section className="brand-mesh border-b border-border">
          <div className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 py-12 md:py-16">
            {page.eyebrow && <p className="eyebrow mb-3">{page.eyebrow}</p>}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl" data-testid="seo-h1">
              {page.h1}
            </h1>
            {showFreshness && (
              <p className="mt-4 text-xs font-mono text-muted-foreground inline-flex items-center gap-2" data-testid="seo-freshness">
                <CalendarClock className="w-3.5 h-3.5" />
                Mis à jour le {formatUpdatedFR(page.dateModified || CONTENT_LAST_MODIFIED)} · Applicable depuis le {formatUpdatedFR(DIRECTIVE_APPLICABLE_SINCE)}
              </p>
            )}
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl border-l-4 border-secondary pl-5">
              {page.intro}
            </p>
          </div>
        </section>

        {/* SCAN BOX mise en évidence */}
        {page.scan && (
          <section className="bg-white border-b border-border">
            <div className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 py-10 md:py-14">
              <ScanBox testidPrefix={`seo-${pageKey}`} />
            </div>
          </section>
        )}

        {/* CORPS : H2 en question + réponse directe */}
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 md:px-10 py-12 md:py-16 grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            {page.sections.map(([h2, body], i) => (
              <section key={`sec-${h2.slice(0, 32)}`} className="mb-9" data-testid={`seo-section-${i}`}>
                <h2 className="text-base md:text-lg font-semibold text-primary mb-3">{h2}</h2>
                <p className="text-sm md:text-base leading-relaxed text-foreground/85">{body}</p>
              </section>
            ))}

            {page.bullets?.length > 0 && (
              <section className="hairline p-6 rounded-xl bg-white mb-9" data-testid="seo-bullets">
                <h2 className="text-base md:text-lg font-semibold text-primary mb-4">{page.bulletsTitle}</h2>
                <ul className="space-y-2.5">
                  {page.bullets.map((b) => (
                    <li key={b.slice(0, 40)} className="flex gap-2.5 text-sm text-foreground/85 leading-relaxed">
                      <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-secondary" /> {b}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {sourceItems.length > 0 && (
              <section className="border-l-4 border-primary/30 pl-5 mb-9" data-testid="seo-sources">
                <h2 className="text-base md:text-lg font-semibold text-primary mb-3 inline-flex items-center gap-2">
                  <Scale className="w-4 h-4" /> {page.sourcesTitle || "Textes et sources"}
                </h2>
                <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
                  {sourceItems.map((s, i) => (
                    <li key={s.href || s.label.slice(0, 40)} className="leading-relaxed">
                      {s.href ? (
                        <a
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline underline-offset-4 inline-flex items-baseline gap-1"
                          data-testid={`seo-source-link-${i}`}
                        >
                          {s.label}
                          <ExternalLink className="w-3 h-3 shrink-0 self-center" />
                        </a>
                      ) : (
                        <span className="font-mono">{s.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {page.faq?.length > 0 && (
              <section className="hairline p-6 rounded-xl bg-white" data-testid="seo-faq">
                <h2 className="text-base md:text-lg font-semibold text-primary mb-4 inline-flex items-center gap-2">
                  <BookOpenCheck className="w-4 h-4" /> {page.faqTitle || "FAQ"}
                </h2>
                {page.faq.map(([q, a], i) => (
                  <details key={q} className="group border-b border-border last:border-0 py-3" data-testid={`seo-faq-item-${i}`}>
                    <summary className="cursor-pointer text-sm font-medium flex justify-between items-center gap-4 list-none">
                      {q}<span className="text-primary group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a}</p>
                  </details>
                ))}
              </section>
            )}
          </div>

          {/* ASIDE : maillage interne + CTA */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start">
            <div className="bg-primary text-white p-6 rounded-xl">
              <img src="/brand/logo-icon.png" alt="Logo EmpCo Compliance — outil de conformité directive UE 2024/825" className="h-9 mb-4 brightness-0 invert opacity-90" />
              <p className="font-serif text-lg leading-snug mb-3">{page.ctaTitle}</p>
              <p className="text-sm text-white/75 mb-5">{page.ctaText}</p>
              <Link to={page.ctaTo || "/"} className="inline-flex items-center gap-2 bg-white text-primary px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-white/90" data-testid="seo-cta">
                {page.ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {links.length > 0 && (
              <div data-testid="seo-internal-links">
                <p className="eyebrow mb-3">{page.linksTitle}</p>
                <ul className="space-y-3">
                  {links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="text-sm hover:text-primary leading-snug block underline-offset-4 hover:underline">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
